"use client";

import { mergeClasses } from "@trex-2b/libs";

const iconSrc = {
  SealCheck: new URL("./assets/SealCheck.svg", import.meta.url).toString(),
  Clock: new URL("./assets/clock.svg", import.meta.url).toString(),
  ArrowLineLeft: new URL(
    "./assets/ArrowLineLeft.svg",
    import.meta.url,
  ).toString(),
  ArrowLineDown: new URL(
    "./assets/ArrowLineDown.svg",
    import.meta.url,
  ).toString(),
  CaretLeft: new URL("./assets/CaretLeft.svg", import.meta.url).toString(),
  CaretDown: new URL("./assets/CaretDown.svg", import.meta.url).toString(),
  CaretUp: new URL("./assets/CaretUp.svg", import.meta.url).toString(),
  ArrowSquareOut: new URL(
    "./assets/ArrowSquareOut.svg",
    import.meta.url,
  ).toString(),
  ArrowRight: new URL("./assets/ArrowRight.svg", import.meta.url).toString(),
  CheckCircle: new URL("./assets/CheckCircle.svg", import.meta.url).toString(),
  CheckMark: new URL("./assets/CheckMark.svg", import.meta.url).toString(),
  ArchiveTray: new URL("./assets/ArchiveTray.svg", import.meta.url).toString(),
  CheckedDefault: new URL(
    "./assets/CheckedDefault.svg",
    import.meta.url,
  ).toString(),
  CheckedHover: new URL(
    "./assets/CheckedHover.svg",
    import.meta.url,
  ).toString(),
  UncheckedDefault: new URL(
    "./assets/UncheckedDefault.svg",
    import.meta.url,
  ).toString(),
  UncheckedHover: new URL(
    "./assets/UncheckedHover.svg",
    import.meta.url,
  ).toString(),
  Chevron: new URL("./assets/chevron.svg", import.meta.url).toString(),
  Cloture: new URL("./assets/cloture.svg", import.meta.url).toString(),
  Close: new URL("./assets/close.svg", import.meta.url).toString(),
  GearSix: new URL("./assets/GearSix.svg", import.meta.url).toString(),
  ShieldCheck: new URL("./assets/ShieldCheck.svg", import.meta.url).toString(),
  WarningCircle: new URL(
    "./assets/WarningCircle.svg",
    import.meta.url,
  ).toString(),
  Email: new URL("./assets/email.svg", import.meta.url).toString(),
  IntersectionTriangle: new URL(
    "./assets/IntersectionTriangle.svg",
    import.meta.url,
  ).toString(),
  Loading: null,
} as const;

export type CommonIconName = keyof typeof iconSrc;

const loadingSpinnerStyle = {
  display: "inline-block",
  flexShrink: 0,
  borderRadius: "50%",
  WebkitMask:
    "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
  mask: "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
  animation: "trex-loading-spinner-spin 1s linear infinite",
} as const;

export type CommonIconProps = {
  iconName: CommonIconName;
  className?: string;
  /**
   * 可选：给单色 SVG 图标着色。
   * 通过 CSS mask 渲染；若 SVG 是多色图形，建议不要传该值。
   */
  iconColor?: string;
  /**
   * 默认为装饰性图标（aria-hidden）。
   * 若需要可访问文本，请传 ariaLabel。
   */
  ariaLabel?: string;
};

export function CommonIcon({
  iconName,
  className,
  ariaLabel,
  iconColor,
}: CommonIconProps) {
  if (iconName === "Loading") {
    const arcColor = iconColor ?? "currentColor";
    return (
      <>
        <style>{`@keyframes trex-loading-spinner-spin { to { transform: rotate(360deg); } }`}</style>
        <span
          role="status"
          aria-label={ariaLabel ?? "Loading"}
          className={mergeClasses("inline-block size-3 shrink-0", className)}
          style={{
            ...loadingSpinnerStyle,
            background: `conic-gradient(from 0deg, transparent 0deg 60deg, ${arcColor} 60deg 300deg, transparent 300deg)`,
          }}
        />
      </>
    );
  }

  const src = iconSrc[iconName];
  if (src == null) return null;

  const maskStyle =
    iconColor != null
      ? ({
          backgroundColor: iconColor,
          WebkitMaskImage: `url('${src}')`,
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          WebkitMaskPosition: "center",
          maskImage: `url('${src}')`,
          maskRepeat: "no-repeat",
          maskSize: "contain",
          maskPosition: "center",
        } as const)
      : ({
          backgroundImage: `url('${src}')`,
        } as const);

  if (ariaLabel) {
    return (
      <span
        role="img"
        aria-label={ariaLabel}
        className={mergeClasses(
          "block bg-center bg-no-repeat bg-contain",
          className,
        )}
        style={maskStyle}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={mergeClasses(
        "block bg-center bg-no-repeat bg-contain",
        className,
      )}
      style={maskStyle}
    />
  );
}
